import React, {forwardRef} from 'react'
import {ButtonBase, ButtonProps} from '.'
import {ForwardRefComponent as PolymorphicForwardRefComponent} from '../../utils/polymorphic'

/**
 *
 *
 * Button is a low-level primitive that provides WAI-ARIA accessibility semantics, dependable keyboard interactions, consistent focus behavior, and that normalizes touch, press, and click interactions across different devices and input modalities.
 *
 * Buttons consist of a clickable area usually containing a textual label or an icon that users can click to perform an action. In addition, keyboard users may activate buttons using the Space or Enter keys.
 *
 * It allows user interactions, accessibility, internationalization, and behavior to be reused, while allowing custom styling and rendering to live within the definitions created for each product ecosystem.
 *
 * Button handles mouse, keyboard, and touch interactions, focus behavior, and ARIA props for both native button elements and custom element types.
 *
 * Note: If a visual label is not provided (e.g. an icon only button), then an aria-label or aria-labelledby prop must be passed to identify the button to assistive technology.
 *
 */
const Button = forwardRef(({children, ...props}: ButtonProps, forwardedRef): JSX.Element => {
  return (
    <ButtonBase ref={forwardedRef} as="button" {...props}>
      {children}
    </ButtonBase>
  )
}) as PolymorphicForwardRefComponent<'button' | 'a', ButtonProps>

Button.displayName = 'Button'
export default Button
